import { makeStyles } from '@material-ui/core';
import React, { useMemo } from 'react';
import { Mentor } from '../../api/mentors';
import {
  COLORS,
  INTER_FONT_FAMILY,
  MONTSERRAT_FONT_FAMILY,
} from '../../theme/variables';
import { getAbbreviationByName } from '../../utils/functions';

interface MentorCardProfileProps {
  mentor: Mentor;
  logoUrl: string | null;
  specializations: string[];
}

const useStyles = makeStyles((theme) => ({
  page: {
    fontFamily: `${INTER_FONT_FAMILY}, sans-serif`,
    background: COLORS.COLOR_ADDITIONAL_GENERAL_BACKGROUND,
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    padding: '40px 16px',
    boxSizing: 'border-box',

    '@media (max-width: 700px)': {
      padding: '20px 12px',
    },
  },
  card: {
    width: '100%',
    maxWidth: 600,
    background: COLORS.COLOR_ADDITIONAL_WHITE,
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    padding: 32,
    alignSelf: 'flex-start',

    '@media (max-width: 700px)': {
      maxWidth: '100%',
      padding: 20,
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 20,

    '@media (max-width: 700px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 12,
    },
  },
  avatar: {
    width: 88,
    height: 88,
    borderRadius: '50%',
    flexShrink: 0,
    background: COLORS.COLOR_BLUE_BASE,
    color: COLORS.COLOR_ADDITIONAL_WHITE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 36,
    fontWeight: 700,
    fontFamily: MONTSERRAT_FONT_FAMILY,

    '@media (max-width: 700px)': {
      width: 64,
      height: 64,
      fontSize: 28,
    },
  },
  avatarImage: {
    width: 88,
    height: 88,
    borderRadius: '50%',
    objectFit: 'cover',
    flexShrink: 0,

    '@media (max-width: 700px)': {
      width: 64,
      height: 64,
    },
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: COLORS.COLOR_TEXT_BASE,
    lineHeight: '28px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',

    '@media (max-width: 700px)': {
      fontSize: 18,
      lineHeight: '24px',
    },
  },
  headerText: {
    minWidth: 0,
    flex: 1,
  },
  headerSubtitle: {
    fontSize: 14,
    lineHeight: '20px',
    color: COLORS.COLOR_TEXT_LIGHTENED_20,
    marginTop: 4,

    '@media (max-width: 700px)': {
      fontSize: 12,
      lineHeight: '18px',
    },
  },
  headerRole: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.COLOR_TEXT_LIGHTENED_20,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,

    '@media (max-width: 700px)': {
      fontSize: 11,
      marginBottom: 6,
    },
  },
  about: {
    marginTop: 20,

    '@media (max-width: 700px)': {
      marginTop: 16,
    },
  },
  aboutText: {
    fontSize: 14,
    lineHeight: '22px',
    color: COLORS.COLOR_TEXT_BASE,
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',

    '@media (max-width: 700px)': {
      fontSize: 13,
      lineHeight: '20px',
    },
  },
  details: {
    marginTop: 20,
    padding: '16px 0',
    borderTop: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
    borderBottom: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
    display: 'flex',
    gap: 24,
    flexWrap: 'wrap',

    '@media (max-width: 700px)': {
      marginTop: 16,
      padding: '12px 0',
      gap: 12,
    },
  },
  detailsItem: {
    flex: 1,
    minWidth: 0,
  },
  detailsLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.COLOR_TEXT_LIGHTENED_20,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,

    '@media (max-width: 700px)': {
      fontSize: 11,
    },
  },
  detailsValue: {
    fontSize: 14,
    color: COLORS.COLOR_TEXT_BASE,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',

    '@media (max-width: 700px)': {
      fontSize: 13,
    },
  },
  badge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: 16,
    fontSize: 13,
    lineHeight: '20px',
    marginRight: 8,
    marginBottom: 5,
  },
  badgeEntrepreneur: {
    color: COLORS.COLOR_ADDITIONAL_ORANGE,
    background: COLORS.COLOR_ADDITIONAL_ORANGE_LIGHTENED,
  },
  badgeInvestor: {
    color: COLORS.COLOR_GREEN_BASE,
    background: COLORS.COLOR_GREEN_LIGHTENED_45,
  },
  chipsSection: {
    marginTop: 20,

    '@media (max-width: 700px)': {
      marginTop: 16,
    },
  },
  chipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',

    '@media (max-width: 700px)': {
      gap: '6px',
    },
  },
  chip: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: 16,
    fontSize: 13,
    lineHeight: '20px',
    color: COLORS.COLOR_BLUE_BASE,
    background: COLORS.COLOR_BLUE_LIGHTENED_50,
    border: `1px solid ${COLORS.COLOR_BLUE_LIGHTENED_40}`,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',

    '@media (max-width: 700px)': {
      fontSize: 12,
      lineHeight: '18px',
    },
  },
  availability: {
    textAlign: 'center',
    paddingTop: 20,

    '@media (max-width: 700px)': {
      paddingTop: 12,
    },
  },
  availabilityBadge: {
    display: 'inline-block',
    backgroundColor: COLORS.COLOR_ADDITIONAL_WHITE,
    color: COLORS.COLOR_TEXT_BASE,
    border: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
    fontSize: 14,
    fontWeight: 500,
    padding: '14px 28px',
    borderRadius: 8,
    [theme.breakpoints.down('sm')]: {
      fontSize: 13,
      padding: '10px 20px',
    },
  },
}));

const splitSkills = (skills: string | null): string[] => {
  if (!skills) return [];
  return skills
    .split(/[,;.|]+/)
    .map((skill) => skill.trim())
    .filter(Boolean);
};

const uniqueList = (items: string[]): string[] =>
  Array.from(new Set(items.map((item) => item.trim()).filter(Boolean)));

function MentorCardProfile({
  mentor,
  logoUrl,
  specializations,
}: MentorCardProfileProps) {
  const classes = useStyles();
  const fullName = `${mentor.firstName} ${mentor.lastName}`;
  const initials = getAbbreviationByName(mentor.firstName, mentor.lastName);
  const specializationsList = useMemo(
    () => uniqueList(specializations),
    [specializations],
  );
  const skillsList = useMemo(
    () => uniqueList(splitSkills(mentor.skills)),
    [mentor.skills],
  );

  return (
    <div className={classes.page}>
      <div className={classes.card}>
        <div className={classes.header}>
          {logoUrl ? (
            <img className={classes.avatarImage} src={logoUrl} alt={fullName} />
          ) : (
            <div className={classes.avatar}>{initials}</div>
          )}
          <div className={classes.headerText}>
            <div className={classes.headerTitle}>{fullName}</div>
            {(mentor.currentRole || mentor.companyName) && (
              <div className={classes.headerSubtitle}>
                {mentor.currentRole && (
                  <div className={classes.headerRole}>{mentor.currentRole}</div>
                )}
                {mentor.companyName && <div>{mentor.companyName}</div>}
              </div>
            )}
          </div>
        </div>

        {mentor.about && (
          <div className={classes.about}>
            <div className={classes.sectionLabel}>About</div>
            <div className={classes.aboutText}>{mentor.about}</div>
          </div>
        )}

        {(mentor.experienceYears != null ||
          mentor.seniority ||
          mentor.isEnterpreneur ||
          mentor.isInvestor) && (
            <div className={classes.details}>
              {mentor.experienceYears != null && (
                <div className={classes.detailsItem}>
                  <div className={classes.detailsLabel}>Experience</div>
                  <div className={classes.detailsValue}>
                    {mentor.experienceYears} years
                  </div>
                </div>
              )}
              {mentor.seniority && (
                <div className={classes.detailsItem}>
                  <div className={classes.detailsLabel}>Seniority</div>
                  <div className={classes.detailsValue}>{mentor.seniority}</div>
                </div>
              )}
              {(mentor.isEnterpreneur || mentor.isInvestor) && (
                <div className={classes.detailsItem}>
                  {mentor.isEnterpreneur && (
                    <span
                      className={`${classes.badge} ${classes.badgeEntrepreneur}`}
                    >
                      Entrepreneur
                    </span>
                  )}
                  {mentor.isInvestor && (
                    <span className={`${classes.badge} ${classes.badgeInvestor}`}>
                      Investor
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

        {specializationsList.length > 0 && (
          <div className={classes.chipsSection}>
            <div className={classes.sectionLabel}>Industry Domains</div>
            <div className={classes.chipsContainer}>
              {specializationsList.map((item) => (
                <span key={item} className={classes.chip}>
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}

        {skillsList.length > 0 && (
          <div className={classes.chipsSection}>
            <div className={classes.sectionLabel}>Skills &amp; Expertise</div>
            <div className={classes.chipsContainer}>
              {skillsList.map((item) => (
                <span key={item} className={classes.chip}>
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}

        {mentor.mentorCapacity != null && mentor.mentorCapacity > 0 && (
          <div className={classes.availability}>
            <div className={classes.availabilityBadge}>
              Available to mentor {mentor.mentorCapacity} teams
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MentorCardProfile;
export type { MentorCardProfileProps };
